import type { SspId, SsuId, TenantContext } from '@cguard/types';
import type {
  AssuranceLevel,
  CguardClaims,
  HydratedAuthSession,
  IdentityCapabilities,
  PermissionGrant,
  ScopeGrant,
  TenantPositionTier,
} from './types';

type SessionUser = {
  readonly profile: {
    readonly sub?: string;
    readonly email?: string;
    readonly name?: string;
  };
  readonly expires_at?: number;
  readonly access_token: string;
};

const IDENTITY_ME_PATH = '/v1/identity/me';
const CROSS_TENANT_PERMISSION = 'realtime.cross-tenant-subscribe';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const DOTNET_GUID_PATTERN = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/iu;

export type IdentityMeErrorKind = 'unauthorized' | 'forbidden' | 'unexpected-response';

/** A typed failure returned while loading the server-owned authorization projection. */
export class IdentityMeError extends Error {
  readonly kind: IdentityMeErrorKind;
  readonly status: number;

  constructor(kind: IdentityMeErrorKind, status: number) {
    super(`GET ${IDENTITY_ME_PATH} returned HTTP ${status}.`);
    this.name = 'IdentityMeError';
    this.kind = kind;
    this.status = status;
  }
}

/**
 * Build a session from standard OIDC identity fields and a validated `/me`
 * projection. JWT role and `cguard:*` claims are intentionally ignored.
 */
export function sessionFromUser(
  user: SessionUser,
  capabilities: IdentityCapabilities,
): HydratedAuthSession {
  const profile = user.profile;
  const tenant = legacyTenantFromCapabilities(capabilities);
  const cguardClaims: CguardClaims = {
    userId: capabilities.userId,
    tier: tenant.tier,
    tenantChain: [],
    ...(tenant.sspId ? { sspId: tenant.sspId } : {}),
    ...(tenant.ssuId ? { ssuId: tenant.ssuId } : {}),
  };

  return {
    subject: profile.sub ?? '',
    email: profile.email ?? '',
    ...(profile.name ? { displayName: profile.name } : {}),
    ...capabilities,
    roles: capabilities.roleNames,
    tenant,
    cguardClaims,
    accessTokenExp: user.expires_at ?? 0,
    accessToken: user.access_token,
  };
}

/** Validate a raw `/me` body before combining it with standard OIDC identity. */
export function sessionFromIdentityProjection(
  user: SessionUser,
  projection: unknown,
): HydratedAuthSession {
  return sessionFromUser(user, parseIdentityCapabilities(projection));
}

/** Load and validate `/me`, then combine it with a fresh OIDC user. */
export async function loadAuthSession(
  user: SessionUser,
  fetcher: typeof fetch = fetch,
): Promise<HydratedAuthSession> {
  const response = await fetcher(IDENTITY_ME_PATH, {
    method: 'GET',
    headers: {
      accept: 'application/json',
      authorization: `Bearer ${user.access_token}`,
    },
  });
  if (!response.ok) {
    const kind: IdentityMeErrorKind =
      response.status === 401
        ? 'unauthorized'
        : response.status === 403
          ? 'forbidden'
          : 'unexpected-response';
    throw new IdentityMeError(kind, response.status);
  }

  const body: unknown = await response.json();
  return sessionFromIdentityProjection(user, body);
}

function parseIdentityCapabilities(value: unknown): IdentityCapabilities {
  if (!isRecord(value)) throw invalidProjection();
  const userId = readNonEmptyString(value, 'userId');
  const tier = readNullableTier(value, 'tier');
  const sspId = readNullableGuid(value, 'sspId');
  const ssuId = readNullableGuid(value, 'ssuId');
  const siteId = readNullableGuid(value, 'siteId');
  const roleNames = readStringArray(value, 'roleNames');
  const permissionValues = readArray(value, 'permissions');
  const scopeValues = readArray(value, 'scopeGrants');
  const authzVersion = Reflect.get(value, 'authzVersion');
  if (
    userId === null ||
    tier === undefined ||
    sspId === undefined ||
    ssuId === undefined ||
    siteId === undefined ||
    roleNames === null ||
    permissionValues === null ||
    scopeValues === null ||
    typeof authzVersion !== 'number' ||
    !Number.isSafeInteger(authzVersion) ||
    authzVersion < 0
  ) {
    throw invalidProjection();
  }

  const permissions: PermissionGrant[] = [];
  for (const permissionValue of permissionValues) {
    const permission = parsePermission(permissionValue);
    if (permission === null) throw invalidProjection();
    permissions.push(permission);
  }

  const scopeGrants: ScopeGrant[] = [];
  for (const scopeValue of scopeValues) {
    const scope = parseScopeGrant(scopeValue);
    if (scope === null) throw invalidProjection();
    scopeGrants.push(scope);
  }

  return {
    userId,
    tier,
    sspId,
    ssuId,
    siteId,
    roleNames,
    permissions,
    scopeGrants,
    authzVersion,
  };
}

function parsePermission(value: unknown): PermissionGrant | null {
  if (!isRecord(value)) return null;
  const permissionKey = readNonEmptyString(value, 'permissionKey');
  const requiredLoa = Reflect.get(value, 'requiredLoa');
  if (permissionKey === null || !isAssuranceLevel(requiredLoa)) return null;
  return { permissionKey, requiredLoa };
}

function parseScopeGrant(value: unknown): ScopeGrant | null {
  if (!isRecord(value)) return null;
  const roleName = readNonEmptyString(value, 'roleName');
  const scopeType = readNonEmptyString(value, 'scopeType');
  const scopeId = readNullableString(value, 'scopeId');
  const granteeSspId = readNullableString(value, 'granteeSspId');
  const granteeSsuId = readNullableString(value, 'granteeSsuId');
  if (
    roleName === null ||
    scopeType === null ||
    scopeId === undefined ||
    granteeSspId === undefined ||
    granteeSsuId === undefined
  ) {
    return null;
  }
  return { roleName, scopeType, scopeId, granteeSspId, granteeSsuId };
}

function legacyTenantFromCapabilities(capabilities: IdentityCapabilities): TenantContext {
  if (
    capabilities.permissions.some(
      (permission) => permission.permissionKey === CROSS_TENANT_PERMISSION,
    )
  ) {
    return { tier: 'sira' };
  }

  const sspScope = capabilities.scopeGrants.find(
    (grant) => grant.scopeType.toLowerCase() === 'ssp' && grant.scopeId !== null,
  );
  if (sspScope?.scopeId) {
    const sspId = toSspId(sspScope.scopeId);
    return sspId ? { tier: 'ssp', sspId } : { tier: 'ssp' };
  }

  const anchoredScope = capabilities.scopeGrants.find(
    (grant) => grant.granteeSspId !== null || grant.granteeSsuId !== null,
  );
  const sspId = toSspId(anchoredScope?.granteeSspId ?? null);
  const ssuId = toSsuId(anchoredScope?.granteeSsuId ?? null);
  return {
    tier: 'ssu',
    ...(sspId ? { sspId } : {}),
    ...(ssuId ? { ssuId } : {}),
  };
}

function toSspId(value: string | null): SspId | undefined {
  return value !== null && UUID_PATTERN.test(value) ? (value as SspId) : undefined;
}

function toSsuId(value: string | null): SsuId | undefined {
  return value !== null && UUID_PATTERN.test(value) ? (value as SsuId) : undefined;
}

function isRecord(value: unknown): value is object {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function readNonEmptyString(value: object, key: string): string | null {
  const field = Reflect.get(value, key);
  return typeof field === 'string' && field.trim().length > 0 ? field : null;
}

function readStringArray(value: object, key: string): string[] | null {
  const values = readArray(value, key);
  if (values === null || values.some((item) => typeof item !== 'string' || item.length === 0)) {
    return null;
  }
  return values.filter((item): item is string => typeof item === 'string');
}

function readArray(value: object, key: string): unknown[] | null {
  const field = Reflect.get(value, key);
  return Array.isArray(field) ? field : null;
}

function readNullableString(value: object, key: string): string | null | undefined {
  if (!Object.prototype.hasOwnProperty.call(value, key)) return undefined;
  const field = Reflect.get(value, key);
  return field === null || (typeof field === 'string' && field.length > 0) ? field : undefined;
}

// An absent tenant-position key reads as null, not as a parse failure. SPA images and
// the API image roll out independently, so a browser can briefly hold a build newer
// than the pod answering `/me`; rejecting the response would lock every user out of
// every console for the length of that window. Absent and null mean the same thing —
// the server did not state a tenant position — and neither widens anything. A key that
// IS present but malformed stays a hard error: that is a contract breach, not skew.
function readNullableGuid(value: object, key: string): string | null | undefined {
  if (!Object.prototype.hasOwnProperty.call(value, key)) return null;
  const field = readNullableString(value, key);
  return field === null || (field !== undefined && DOTNET_GUID_PATTERN.test(field))
    ? field
    : undefined;
}

function readNullableTier(value: object, key: string): TenantPositionTier | null | undefined {
  if (!Object.prototype.hasOwnProperty.call(value, key)) return null;
  const field = readNullableString(value, key);
  return field === null || isTenantPositionTier(field) ? field : undefined;
}

function isTenantPositionTier(value: unknown): value is TenantPositionTier {
  return (
    value === 'sira' ||
    value === 'ssp' ||
    value === 'ssu' ||
    value === 'operator' ||
    value === 'audit-viewer' ||
    value === 'approval-authority'
  );
}

function isAssuranceLevel(value: unknown): value is AssuranceLevel {
  return value === 1 || value === 2 || value === 3;
}

function invalidProjection(): Error {
  return new Error(`GET ${IDENTITY_ME_PATH} returned an invalid capability projection.`);
}
